import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDownAZ, ArrowUpAZ, Search } from "lucide-react";

// ASSETS
import LeftArrow from "assets/icons/ArrowLeftHovered.svg";
import CoverPlaceholder from "assets/img/CoverPlaceholder.png";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToWrapper,
  ContentSectionWrapper,
  HeaderSectionWrapper,
  InternationalPartnersCardIcon,
  NoDataContainer,
  PartnersCardIcon,
  PartnersCardIconWrapper,
  PartnersCardTextWrapper,
  PartnersCardWrapper,
  PartnersCardsWrapper,
  PartnersContainer,
  SvgContainer,
  TitleSectionWrapper,
  TitleWrapper,
  SearchToolbar,
  ToolbarButton,
  SortButton,
  ResultsCount,
} from "./PartnersStyled";

// LIBRARIES
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

// MISC
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import { ModalTypes } from "components/Modal/ModalModel";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import Pagination from "components/Atoms/Paginations/Pagination";
import HeaderTab from "components/HeaderTab/HeaderTab";
import CustomText from "components/Atoms/CustomText/CustomText";
import Modal from "components/Modal/Modal";
import InputAtom from "components/Atoms/InputAtom/InputAtom";
import aboutService from "services/aboutService";
import { uploadBaseUrl } from "services/base";
import { PartnersData, PartnerViewModel } from "./PartnersModel";
import { filterAndSortPartners, mapPartner, PartnerSortOrder } from "./partnersDirectory";

const partnerTypes = ["national", "international", "organisme-internationale"];

const Partners = () => {
  // PROPERTIES
  const ELEMENTS_PER_PAGE = 12;

  // LIBRARY CONSTANTS
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const linkedType = searchParams.get("type") ?? "national";
  const linkedQuery = searchParams.get("q") ?? "";
  const linkedTab = Math.max(0, partnerTypes.indexOf(linkedType));

  // STATE CONSTANTS
  const [modalState, setModalState] = useState({
    isVisible: false,
    type: "",
    item: null,
  });
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const [selectedTab, setSelectedTab] = useState<number>(linkedTab);
  const [currentPage, setCurrentPage] = useState(1);
  const { t, i18n } = useTranslation();
  const [partners, setPartners] = useState<PartnerViewModel[]>([]);
  const [inputText, setInputText] = useState(linkedQuery);
  const [search, setSearch] = useState(linkedQuery);
  const [sortOrder, setSortOrder] = useState<PartnerSortOrder>("asc");
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [loadRevision, setLoadRevision] = useState(0);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedTab(linkedTab);
    setInputText(linkedQuery);
    setSearch(linkedQuery);
    setCurrentPage(1);
  }, [linkedTab, linkedQuery]);

  // Load the whole category so search and ordering happen before pagination.
  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setHasError(false);
    aboutService
      .getPartnerDirectory(partnerTypes[selectedTab], controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setPartners(response.map((partner) => mapPartner(partner, uploadBaseUrl, CoverPlaceholder)));
        setIsLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setHasError(true);
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [selectedTab, loadRevision]);

  // Tab change handler
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
    setCurrentPage(1); // Reset to page 1 when switching tabs
  };

  const filteredPartners = useMemo(
    () => filterAndSortPartners(partners, search, sortOrder, i18n.language),
    [partners, search, sortOrder, i18n.language]
  );
  const totalPages = Math.ceil(filteredPartners.length / ELEMENTS_PER_PAGE);
  const currentData = filteredPartners.slice((currentPage - 1) * ELEMENTS_PER_PAGE, currentPage * ELEMENTS_PER_PAGE);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSearch(inputText.trim());
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    topRef.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  const content = (
    <PartnersCardsWrapper>
      {isLoading || hasError ? (
        <NoDataContainer role={hasError ? "alert" : "status"}>
          <CustomText text={t(hasError ? "view.partners.loadError" : "common.loading")} type={TextType.P1} />
          {hasError && <ToolbarButton type="button" onClick={() => setLoadRevision((value) => value + 1)}>{t("view.partners.retry")}</ToolbarButton>}
        </NoDataContainer>
      ) : currentData.length > 0 ? (
        currentData.map((partner) => (
          <PartnersCardWrapper
            key={partner.id}
            as="button"
            type="button"
            aria-label={partner.title}
            onClick={() => handleClick(partner.details)}
            biggerCursor={biggerCursor}
          >
            {selectedTab === 1 ? (
              <PartnersCardIconWrapper>
                <InternationalPartnersCardIcon
                  src={partner.logoUrl}
                  alt=""
                />
              </PartnersCardIconWrapper>
            ) : (
              <PartnersCardIconWrapper>
                <PartnersCardIcon
                  src={partner.logoUrl}
                  alt=""
                />
              </PartnersCardIconWrapper>
            )}
            <PartnersCardTextWrapper>
              <CustomText
                text={partner.title}
                type={TextType.H5bold}
                color={COLORS.RaisinBlack}
              />
            </PartnersCardTextWrapper>
          </PartnersCardWrapper>
        ))
      ) : (
        <NoDataContainer role="status">
          <SvgContainer>
            <NoDataDark />
          </SvgContainer>
          <CustomText
            text={search ? t("view.partners.noSearchResults", { query: search }) : t("app.noData")}
            type={TextType.P1}
            color={COLORS.MetalicSilver}
          />
        </NoDataContainer>
      )}
    </PartnersCardsWrapper>
  );

  const tabs = [
    {
      id: 0,
      tabTitle: t("view.partners.nationalPartnersTab"),
      content: content,
    },
    {
      id: 1,
      tabTitle: t("view.partners.internationalPartnersTab"),
      content: content,
    },
    {
      id: 2,
      tabTitle: t("view.partners.internationalOrgansTab"),
      content: content,
    },
  ];

  const handleClick = (partner: PartnersData) => {
    setModalState({
      isVisible: true,
      type: ModalTypes.PARTNERS,
      item: partner,
    });
  };

  return (
    <PartnersContainer ref={topRef}>
      <TitleSectionWrapper>
        <BackToWrapper onClick={() => navigate(-1)} biggerCursor={biggerCursor}>
          <BackToGuidesImage src={LeftArrow} alt="backIcon" />
          <BackToGuidesButton>
            <CustomText
              text={t("view.about.aboutDSU.button")}
              type={TextType.H5boldspaced}
              color={COLORS.RaisinBlack}
            />
          </BackToGuidesButton>
        </BackToWrapper>
        <TitleWrapper>
          <CustomText type={TextType.H2} text={t("view.partners.title")} />
        </TitleWrapper>
      </TitleSectionWrapper>

      <HeaderSectionWrapper>
        <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} selectedTabId={selectedTab} />
      </HeaderSectionWrapper>

      <ContentSectionWrapper>
        <SearchToolbar $biggerCursor={biggerCursor}>
          <form role="search" onSubmit={handleSearch}>
            <InputAtom isSearchBar value={inputText} placeholder={t("view.partners.searchPlaceholder")}
              startAdornment={<Search size={20} color={COLORS.Crimson} aria-hidden="true" />}
              endAdornment={<ToolbarButton type="submit">{t("view.partners.search")}</ToolbarButton>}
              onChange={(event) => {
                const value = event.target.value;
                setInputText(value);
                if (!value.trim()) { setSearch(""); setCurrentPage(1); }
              }} />
          </form>
          {!isLoading && !hasError && (
            <ResultsCount aria-live="polite">
              <CustomText text={t("view.partners.resultCount", { count: filteredPartners.length })} type={TextType.P2} color={COLORS.Gunmetal} />
            </ResultsCount>
          )}
          <SortButton type="button" title={t("view.partners.sortAlphabetically")}
            aria-label={t("view.partners.sortToggle", { order: sortOrder === "asc" ? "A–Z" : "Z–A" })}
            onClick={() => { setSortOrder((order) => order === "asc" ? "desc" : "asc"); setCurrentPage(1); }}>
            {sortOrder === "asc" ? <ArrowDownAZ size={18} aria-hidden="true" /> : <ArrowUpAZ size={18} aria-hidden="true" />}
            {sortOrder === "asc" ? "A–Z" : "Z–A"}
          </SortButton>
        </SearchToolbar>
        {tabs[selectedTab].content}
        {!isLoading && !hasError && totalPages > 1 && (
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </ContentSectionWrapper>
      <Modal
        isVisible={modalState.isVisible}
        type={modalState.type as ModalTypes}
        item={modalState.item}
        onClose={() =>
          setModalState({ isVisible: false, type: ModalTypes.NONE, item: null })
        }
      />
    </PartnersContainer>
  );
};

export default Partners;
